import React, { useEffect, useRef, useState } from 'react';
import { RADIUS, useTheme } from '../theme';

/**
 * P3: fingerritad signatur. Svart bläck på vit botten oavsett tema (den hamnar i en PDF),
 * pekarhändelser för mus, finger och penna, och skarp på skärmar med hög upplösning.
 * onChange får en PNG som data-URL, eller '' när rutan är tom.
 */
export function SignatureCanvas({ onChange, height = 160 }: { onChange: (png: string) => void; height?: number }) {
  const T = useTheme();
  const ref = useRef<HTMLCanvasElement>(null);
  const drawing = useRef<{ x: number; y: number } | null>(null);
  const [hasInk, setHasInkState] = useState(false);
  const inkRef = useRef(false);
  const setHasInk = (v: boolean) => { inkRef.current = v; setHasInkState(v); };

  useEffect(() => {
    const c = ref.current!;
    const size = () => {
      const r = c.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 3);
      c.width = Math.round(r.width * dpr);
      c.height = Math.round(height * dpr);
      const ctx = c.getContext('2d')!;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, r.width, height);
      setHasInk(false);
      onChange('');
    };
    size();
    // En ändrad bredd (vridning) rensar rutan – hellre det än en förvrängd signatur.
    let w = c.getBoundingClientRect().width;
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => { const nw = c.getBoundingClientRect().width; if (Math.abs(nw - w) > 1) { w = nw; size(); } }) : null;
    ro?.observe(c);
    return () => ro?.disconnect();
  }, [height]); // eslint-disable-line react-hooks/exhaustive-deps

  const pos = (e: React.PointerEvent) => {
    const r = ref.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const down = (e: React.PointerEvent) => {
    e.preventDefault();
    ref.current!.setPointerCapture(e.pointerId);
    drawing.current = pos(e);
  };
  const move = (e: React.PointerEvent) => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = pos(e);
    const ctx = ref.current!.getContext('2d')!;
    ctx.strokeStyle = '#141a10';
    ctx.lineWidth = 2.4;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(drawing.current.x, drawing.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    drawing.current = p;
    if (!inkRef.current) setHasInk(true);
  };
  const up = () => {
    if (!drawing.current) return;
    drawing.current = null;
    if (inkRef.current) onChange(ref.current!.toDataURL('image/png'));
  };
  const clear = () => {
    const c = ref.current!;
    const ctx = c.getContext('2d')!;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, c.width, c.height);
    setHasInk(false);
    onChange('');
  };

  return (
    <div>
      <div style={{ position: 'relative', borderRadius: RADIUS.md, overflow: 'hidden', border: `1px solid ${T.borderHover}`, background: '#fff' }}>
        <canvas
          ref={ref}
          data-testid="signature-canvas"
          aria-label="Rita din signatur"
          style={{ display: 'block', width: '100%', height, touchAction: 'none', cursor: 'crosshair' }}
          onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} onPointerLeave={up}
        />
        {!hasInk && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', color: '#63685a', fontSize: 14 }}>
            Rita din signatur med fingret eller musen
          </div>
        )}
        <div style={{ position: 'absolute', left: 16, right: 16, bottom: 28, borderTop: '1px solid #c9ccc0', pointerEvents: 'none' }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
        <button type="button" onClick={clear} disabled={!hasInk}
          style={{ background: 'none', border: 'none', color: T.accentText, fontSize: 14, fontWeight: 600, cursor: hasInk ? 'pointer' : 'default', padding: '8px 4px', opacity: hasInk ? 1 : 0.5, fontFamily: 'inherit' }}>
          Rensa
        </button>
      </div>
    </div>
  );
}
